<div class="palantir-select">
  @if (label) {
    <palantir-text type="description">
      {{ label }}

      {{ isRequired() ? '*' : '' }}
    </palantir-text>
  }

  <div class="palantir-select__container">
    <div
      class="palantir-select__input-container"
      cdkOverlayOrigin
      #trigger="cdkOverlayOrigin"
      #inputContainer
      (click)="setShowSelectedItem(false)"
      (keydown.enter)="setShowSelectedItem(false)"
    >
      <input
        #input
        type="text"
        [placeholder]="placeholder"
        (focus)="setOptionsIsOpen(true)"
        (blur)="setOptionsIsOpen(false)"
        (input)="filter$.next(input.value)"
        [ngClass]="{
          'ng-hide': showSelectedItem(),
          'ng-invalid': control?.invalid,
          'ng-touched': control?.touched,
          'ng-dirty': control?.dirty,
          'ng-disabled': control?.disabled,
        }"
      />

      <div
        class="palantir-select__selected-option"
        [ngClass]="{
          'ng-hide': !showSelectedItem(),
          'ng-selected': showSelectedItem(),
          'ng-invalid': control?.invalid,
          'ng-touched': control?.touched,
          'ng-dirty': control?.dirty,
          'ng-disabled': control?.disabled,
        }"
      >
        @if (_selected()?.icon) {
          <palantir-icon [name]="_selected()?.icon" />
        }

        @if (_selected()?.avatarUrl) {
          <palantir-avatar size="5xs" [fallbackText]="_selected()?.title">
            <img [src]="_selected()?.avatarUrl" [alt]="_selected()?.description" />
          </palantir-avatar>
        }

        @if (_selected()?.title) {
          <palantir-text
            type="title"
            truncate="true"
            nowrap="true"
            [innerHTML]="_selected()?.title | safeHtml"
          />
        }
        <palantir-text
          type="description"
          truncate="true"
          [innerHTML]="_selected()?.description | safeHtml"
        />

        <div class="palantir-flex-auto"></div>

        @let suffixTag = _selected()?.suffixTag;

        @if (suffixTag) {
          <palantir-tag
            class="palantir-select__selected-option--suffix"
            [color]="suffixTag.color"
            [prefix]="suffixTag.prefix"
            [suffix]="suffixTag.suffix"
            [minified]="suffixTag.minified || false"
          >
            {{ suffixTag.label }}
          </palantir-tag>
        }
      </div>
    </div>

    @if (
      (!_selected() || (_selected() && isRequired())) && !(isSearchLoading() && optionsIsOpen())
    ) {
      <palantir-icon class="palantir-select__suffix" name="select-input-outline" />
    }

    @if (
      _selected() && !control?.disabled && !(isSearchLoading() && optionsIsOpen()) && !isRequired()
    ) {
      <palantir-icon
        (click)="deselect()"
        class="palantir-select__suffix palantir-select__deselect"
        name="close-outline"
      />
    }

    @if (isSearchLoading() && optionsIsOpen()) {
      <palantir-icon
        (click)="deselect()"
        class="palantir-select__suffix palantir-select__search"
        name="loading-outline"
      />
    }
  </div>

  <div class="palantir-select__meta">
    <ng-content select="[palantir-select-error], palantir-select-error" />
  </div>

  @if (optionsIsOpen()) {
    <div class="palantir-select__backdrop"></div>
  }

  <ng-template
    cdkConnectedOverlay
    [cdkConnectedOverlayWidth]="inputContainer.offsetWidth"
    [cdkConnectedOverlayOrigin]="trigger"
    [cdkConnectedOverlayOpen]="optionsIsOpen()"
  >
    <div
      class="palantir-select__options palantir-scrollbar"
      #options
      infiniteScroll
      (scrolled)="onScrollDown()"
      [scrollWindow]="false"
      [alwaysCallback]="true"
      [infiniteScrollThrottle]="0"
      [infiniteScrollDisabled]="infiniteScrollDisabled()"
      [ngClass]="{
        'ng-invalid': control?.invalid,
        'ng-touched': control?.touched,
        'ng-dirty': control?.dirty,
      }"
    >
      @if (_filteredOptions().length) {
        @for (_option of _filteredOptions(); track trackByFn($index, _option)) {
          <div
            class="palantir-select__option"
            [class.active]="_option.id === _selected()?.id"
            (mousedown)="select(_option)"
          >
            <div class="palantir-select__option--content">
              @if (_option.icon) {
                <palantir-avatar size="5xs">
                  <palantir-icon [name]="_option.icon" />
                </palantir-avatar>
              }

              @if (_option.avatarUrl) {
                <palantir-avatar size="5xs" [fallbackText]="_option.title">
                  <img [src]="_option.avatarUrl" [alt]="_option.description" />
                </palantir-avatar>
              }

              @if (_option.title) {
                <palantir-text type="title" nowrap="true" [innerHTML]="_option.title | safeHtml">{{
                  _option.title
                }}</palantir-text>
              }
              <palantir-text
                type="description"
                truncate="true"
                [innerHTML]="_option.description | safeHtml"
                >{{ _option.description }}</palantir-text
              >
            </div>

            @let suffixTag = _option.suffixTag;

            @if (suffixTag) {
              <palantir-tag
                class="palantir-select__option--suffix"
                [color]="suffixTag.color"
                [prefix]="suffixTag.prefix"
                [suffix]="suffixTag.suffix"
                [minified]="suffixTag.minified || false"
              >
                {{ suffixTag.label }}
              </palantir-tag>
            }

            @let suffixAction = _option.suffixAction;

            @if (suffixAction) {
              <palantir-icon
                class="palantir-select__option--suffix-action"
                [style]="{
                  color: (suffixAction.color | labels).color,
                }"
                theme="transparent"
                [name]="suffixAction.icon"
                (mousedown)="$event.preventDefault(); $event.stopPropagation()"
                (click)="suffixAction.action(_option)"
              />
            }
          </div>
        }
        @if (isLoadingMoreLoading()) {
          <div class="palantir-select__paginate-loading">
            <palantir-icon name="loading-outline" />
          </div>
        }
      }

      @if (!_filteredOptions().length) {
        <div class="palantir-select__option palantir-select__option--empty">
          <div class="palantir-select__option--content">
            <palantir-icon name="alert-filled" />
            <palantir-text type="description" truncate="true">{{ emptyState }}</palantir-text>
          </div>
        </div>
      }
    </div>
  </ng-template>
</div>
